import { AlarmClock, CalendarDays, CircleAlert, Clock, TriangleAlert, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { OrderStage, OrdersDashboard, ShipDeadline } from '../api/orders';
import { timeLeft } from './ship-by';

/** O que o cartão escolhido filtra: um prazo dos "a enviar" ou uma situação. */
export type BoardSelection =
  | { deadline: ShipDeadline; stage?: undefined }
  | { stage: OrderStage; deadline?: undefined }
  | null;

const hour = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

interface Tile {
  value: string;
  label: string;
  icon: LucideIcon;
  count: number;
  /** Selo do número: a cor diz a urgência; o texto ao lado, o horário. */
  variant: BadgeProps['variant'];
  hint?: string;
}

/**
 * Painel do topo de Pedidos em aberto: o que precisa sair e **até que horas**. O horário de corte é
 * o que a plataforma exige para cada pacote (no ML, o `/sla`: na coleta, o horário da coleta) —
 * "hoje até 14:00, faltam 2h10" é o que o operador precisa ver primeiro. Clicar filtra a lista.
 * `ToggleGroup` do shadcn/ui (ADR-043), como o filtro de lucro do catálogo.
 */
export function DeadlineBoard({
  data,
  value,
  onChange,
}: {
  data: OrdersDashboard;
  value: BoardSelection;
  onChange: (selection: BoardSelection) => void;
}) {
  const { deadlines } = data;
  const today = deadlines.TODAY.nextDeadline;
  const tiles: Tile[] = [
    {
      value: 'deadline:LATE',
      label: 'Atrasados',
      icon: CircleAlert,
      count: deadlines.LATE.count,
      variant: deadlines.LATE.count > 0 ? 'destructive' : 'neutral',
      hint: deadlines.LATE.count > 0 ? 'passou do horário de corte' : 'nenhum fora do prazo',
    },
    {
      value: 'deadline:TODAY',
      label: 'Enviar hoje',
      icon: AlarmClock,
      count: deadlines.TODAY.count,
      variant: deadlines.TODAY.count > 0 ? 'warning' : 'neutral',
      ...(today
        ? { hint: `próximo corte ${hour(today)} · faltam ${timeLeft(new Date(), new Date(today))}` }
        : {}),
    },
    {
      value: 'deadline:TOMORROW',
      label: 'Enviar amanhã',
      icon: Clock,
      count: deadlines.TOMORROW.count,
      variant: 'info-soft',
      ...(deadlines.TOMORROW.nextDeadline
        ? { hint: `primeiro corte ${hour(deadlines.TOMORROW.nextDeadline)}` }
        : {}),
    },
    {
      value: 'deadline:LATER',
      label: 'Próximos dias',
      icon: CalendarDays,
      count: deadlines.LATER.count,
      variant: 'neutral',
    },
    {
      value: 'stage:IN_TRANSIT',
      label: 'Em trânsito',
      icon: Truck,
      count: data.inTransit,
      variant: 'neutral',
    },
    {
      value: 'stage:PROBLEM',
      label: 'Com problema',
      icon: TriangleAlert,
      count: data.problem,
      variant: data.problem > 0 ? 'destructive' : 'neutral',
      hint: 'não entregue ou envio cancelado',
    },
  ];
  // Sem prazo informado e aguardando pagamento só aparecem quando existem: raros, e sem horário.
  if (deadlines.NONE.count > 0)
    tiles.splice(4, 0, {
      value: 'deadline:NONE',
      label: 'A enviar sem prazo',
      icon: Clock,
      count: deadlines.NONE.count,
      variant: 'neutral',
      hint: 'a plataforma não informou o corte',
    });
  if (data.paymentPending > 0)
    tiles.push({
      value: 'stage:PAYMENT_PENDING',
      label: 'Aguardando pagamento',
      icon: Clock,
      count: data.paymentPending,
      variant: 'neutral',
    });

  const current = value?.deadline
    ? `deadline:${value.deadline}`
    : value?.stage
      ? `stage:${value.stage}`
      : '';
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      aria-label="Pedidos por prazo de envio"
      value={current}
      onValueChange={(next) => {
        const [kind, key] = next.split(':');
        onChange(
          kind === 'deadline'
            ? { deadline: key as ShipDeadline }
            : kind === 'stage'
              ? { stage: key as OrderStage }
              : null,
        );
      }}
      className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6"
      spacing={2}
    >
      {tiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <ToggleGroupItem
            key={tile.value}
            value={tile.value}
            aria-label={`${tile.label}: ${tile.count}`}
            className="h-full flex-col items-start justify-start gap-1 self-stretch p-3 text-left"
          >
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Icon aria-hidden className="size-3.5" /> {tile.label}
            </span>
            <Badge variant={tile.variant} className="tabular px-2 text-lg font-semibold">
              {tile.count}
            </Badge>
            {tile.hint && (
              <span className="text-xs font-normal whitespace-normal text-muted-foreground">
                {tile.hint}
              </span>
            )}
          </ToggleGroupItem>
        );
      })}
    </ToggleGroup>
  );
}
